import axiosInstance from './axiosInstance';

class AuthService {
  // Initiate login - redirect to login page
  initiateLogin() {
    const returnUrl = window.location.pathname + window.location.search;
    localStorage.setItem('authReturnUrl', returnUrl);
    window.location.href = '/login';
  }

  // Initiate Google OAuth login
  initiateGoogleLogin() {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5041';
    window.location.href = `${baseURL}/api/auth/google`;
  }

  // Manual registration
  async register(email, password, name) {
    try {
      const response = await axiosInstance.post('/api/auth/register', {
        email,
        password,
        name
      });

      // Store tokens
      this.setTokens(response.data.token, response.data.refreshToken);

      return {
        success: true,
        user: response.data.user
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Registration failed. Please try again.'
      };
    }
  }

  // Manual login
  async login(email, password) {
    try {
      const response = await axiosInstance.post('/api/auth/login', {
        email,
        password
      });

      // Store tokens
      this.setTokens(response.data.token, response.data.refreshToken);

      return {
        success: true,
        user: response.data.user
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Login failed. Please try again.'
      };
    }
  }

  // Get current user info
  async getCurrentUser() {
    try {
      const response = await axiosInstance.get('/api/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Logout user
  async logout() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await axiosInstance.post('/api/auth/logout', { refreshToken });
      }
    } catch (error) {
      // Proceed with logout even if API call fails
    } finally {
      // Always clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  }

  // Get stored auth token
  getToken() {
    return localStorage.getItem('authToken');
  }

  // Store auth tokens
  setTokens(authToken, refreshToken) {
    localStorage.setItem('authToken', authToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  // Clear all auth data
  clearAuth() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  }

  // Check if current token is expired (optional - for UI hints)
  isTokenExpired(bufferMinutes = 1) {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000;
      const now = Date.now();
      const buffer = bufferMinutes * 60 * 1000;

      return now >= (expiry - buffer);
    } catch (error) {
      return true;
    }
  }

  // Get token expiry time
  getTokenExpiry() {
    const token = localStorage.getItem('authToken');
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000);
    } catch (error) {
      return null;
    }
  }

  // Check if user needs authentication
  needsAuthentication() {
    return !this.isAuthenticated() || this.isTokenExpired();
  }

  // Handle redirect after successful login
  handleLoginSuccess() {
    const returnUrl = localStorage.getItem('authReturnUrl');
    localStorage.removeItem('authReturnUrl');

    if (returnUrl && returnUrl !== '/login' && returnUrl !== '/') {
      window.location.href = returnUrl;
    } else {
      window.location.href = '/dashboard';
    }
  }

  // Get the stored return URL
  getReturnUrl() {
    return localStorage.getItem('authReturnUrl') || '/';
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
